import { Coords, ItemGetClientPacket, MapTileSpec, SitState } from 'eolib';
import type { Client } from '@/client';
import {openLifeCampfire,sendLifeAction} from '@/life-skills';
import {
  getBoardIntersecting,
  getCharacterIntersecting,
  getCharacterRectangle,
  getDoorIntersecting,
  getJukeboxIntersecting,
  getLockerIntersecting,
  getNpcIntersecting,
  getSignIntersecting,
} from '@/collision';
import {
  PLAYER_MENU_ITEM_HEIGHT,
  PLAYER_MENU_OFFSET_Y,
  PLAYER_MENU_WIDTH,
} from '@/consts';
import { EOResourceID } from '@/edf';
import { GameState, PlayerMenuItem } from '@/game-state';
import { CursorClickAnimation } from '@/render';
import { SfxId } from '@/sfx';
import { capitalize } from '@/utils';
import type { Vector2 } from '@/vector';

export class MouseController {
  private client: Client;

  private ignoreNextClick = false;
  setIgnoreNextClick() {
    this.ignoreNextClick = true;
  }

  private touchStartPosition: Vector2 | null = null;
  private touchStartMap: Client['map'] | undefined;

  private canUseWorld(): boolean {
    return this.client.state === GameState.InGame &&
      !this.client.worldLoading && !!this.client.map;
  }

  constructor(client: Client) {
    this.client = client;

    let longPressTimer = 0;
    let isLongPress = false;

    const isInUI = (target: EventTarget | null) => {
      const ui = document.getElementById('ui');
      return ui && target && target !== ui && ui.contains(target as Node);
    };

    const getCoordsFromTouch = (touch: Touch): Vector2 | null => {
      if (!this.client.app) return null;
      const canvas = this.client.app.renderer.canvas;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return {
        x: Math.min(
          Math.max(Math.floor((touch.clientX - rect.left) * scaleX), 0),
          canvas.width,
        ),
        y: Math.min(
          Math.max(Math.floor((touch.clientY - rect.top) * scaleY), 0),
          canvas.height,
        ),
      };
    };

    window.addEventListener(
      'touchstart',
      (e) => {
        this.touchStartPosition = null;
        this.touchStartMap = undefined;
        clearTimeout(longPressTimer);
        if (!this.canUseWorld() || isInUI(e.target) || e.touches.length !== 1) return;
        this.touchStartMap = this.client.map;
        e.preventDefault();
        isLongPress = false;

        const coords = getCoordsFromTouch(e.touches[0]);
        if (coords) {
          this.touchStartPosition = {
            x: e.touches[0].clientX,
            y: e.touches[0].clientY,
          };
          this.client.setMousePosition(coords);
        }

        longPressTimer = window.setTimeout(() => {
          if (!this.canUseWorld() || this.touchStartMap !== this.client.map) return;
          isLongPress = true;
          this.handleRightClick({ target: e.target } as MouseEvent);
        }, 500);
      },
      { passive: false },
    );

    window.addEventListener(
      'touchmove',
      (e) => {
        if (!this.touchStartPosition || isInUI(e.target)) return;
        e.preventDefault();
        clearTimeout(longPressTimer);
        const coords = getCoordsFromTouch(e.touches[0]);
        if (coords) {
          this.client.setMousePosition(coords);
        }
      },
      { passive: false },
    );

    window.addEventListener(
      'touchend',
      (e) => {
        clearTimeout(longPressTimer);
        const start = this.touchStartPosition;
        const startMap = this.touchStartMap;
        this.touchStartPosition = null;
        this.touchStartMap = undefined;
        if (!start || !this.canUseWorld() || startMap !== this.client.map || isInUI(e.target) || !e.changedTouches.length) return;
        e.preventDefault();
        const coords = getCoordsFromTouch(e.changedTouches[0]);
        if (coords) this.client.setMousePosition(coords);
        if (!isLongPress) {
          const diffX =
            e.changedTouches[0].clientX - start.x;
          const diffY =
            e.changedTouches[0].clientY - start.y;
          if (Math.sqrt(diffX * diffX + diffY * diffY) > 10) {
            // Consider it a swipe, not a tap
            return;
          }
          this.handleClick({ target: e.target } as MouseEvent);
        }
        isLongPress = false;
      },
      { passive: false },
    );

    window.addEventListener('touchcancel', () => {
      this.touchStartPosition = null;
      this.touchStartMap = undefined;
      clearTimeout(longPressTimer);
      isLongPress = false;
    });

    window.addEventListener('mousemove', (e) => {
      if (!this.client.app?.renderer?.canvas) return;
      const canvas = this.client.app.renderer.canvas;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      this.client.setMousePosition({
        x: Math.min(
          Math.max(Math.floor((e.clientX - rect.left) * scaleX), 0),
          canvas.width,
        ),
        y: Math.min(
          Math.max(Math.floor((e.clientY - rect.top) * scaleY), 0),
          canvas.height,
        ),
      });
    });

    let mouseDownPosition: { x: number; y: number; map: Client['map'] } | null = null;

    window.addEventListener(
      'pointerdown',
      (e) => {
        if (e.pointerType !== 'mouse') return;
        // Only track left-click drags
        if (e.button !== 0) return;
        // Don't track drags that start inside the UI
        if (!this.canUseWorld() || isInUI(e.target)) {
          mouseDownPosition = null;
          return;
        }
        mouseDownPosition = { x: e.clientX, y: e.clientY, map: this.client.map };
      },
      { capture: true },
    );

    window.addEventListener('pointerup', (e) => {
      if (e.pointerType !== 'mouse') return;
      // Only handle left-click releases; right-click is handled via contextmenu
      if (e.button !== 0) return;

      // Require a tracked pointerdown — if it started in UI or was blocked
      // by stopPropagation, mouseDownPosition is null and we skip entirely.
      if (!mouseDownPosition) return;
      if (!this.canUseWorld() || mouseDownPosition.map !== this.client.map || isInUI(e.target)) {
        mouseDownPosition = null;
        return;
      }
      const coords = getCoordsFromTouch(e as unknown as Touch);
      if (coords) this.client.setMousePosition(coords);

      const dx = e.clientX - mouseDownPosition.x;
      const dy = e.clientY - mouseDownPosition.y;
      mouseDownPosition = null;
      if (Math.sqrt(dx * dx + dy * dy) > 10) {
        // Treat as a drag, not a click — also clear any pending ignore flag
        this.ignoreNextClick = false;
        return;
      }

      if (this.ignoreNextClick) {
        this.ignoreNextClick = false;
        return;
      }

      this.handleClick(e as unknown as MouseEvent);
    });

    window.addEventListener('pointercancel', (e) => {
      if (e.pointerType !== 'mouse') return;
      mouseDownPosition = null;
      this.ignoreNextClick = false;
    });

    window.addEventListener('contextmenu', (e) => {
      this.handleRightClick(e);
      e.preventDefault();
    });
  }

  handleClick(e: MouseEvent): void {
    if (!this.canUseWorld()) return;
    if (this.ignoreNextClick) {
      this.ignoreNextClick = false;
      return;
    }

    if (this.client.menuPlayerId) {
      const hovered = this.getHoveredPlayerMenuItem();
      if (hovered !== undefined) {
        this.client.audioController.playById(SfxId.ButtonClick);
        switch (hovered) {
          case PlayerMenuItem.Paperdoll:
            this.client.socialController.requestPaperdoll(
              this.client.menuPlayerId,
            );
            break;
          case PlayerMenuItem.Book:
            this.client.socialController.requestBook(this.client.menuPlayerId);
            break;
          case PlayerMenuItem.Whisper: {
            const character = this.client.getCharacterById(
              this.client.menuPlayerId,
            );
            if (character) {
              this.client.chatController.notifySetChat(`!${character.name} `);
            }
            break;
          }
          case PlayerMenuItem.Join:
            this.client.partyController.requestToJoin(this.client.menuPlayerId);
            break;
          case PlayerMenuItem.Invite:
            this.client.partyController.invite(this.client.menuPlayerId);
            break;
          case PlayerMenuItem.Trade:
            this.client.tradeController.request(this.client.menuPlayerId);
            break;
        }
        this.client.menuPlayerId = 0;
        return;
      }

      this.client.menuPlayerId = 0;
    }

    const ui = document.getElementById('ui')!;
    if (
      this.client.state !== GameState.InGame ||
      this.client.typing ||
      (e.target !== ui && ui.contains(e.target as Node))
    ) {
      return;
    }

    if (
      [SitState.Floor, SitState.Chair].includes(
        this.client.getPlayerCharacter()!.sitState,
      )
    ) {
      this.client.movementController.stand();
      return;
    }

    if (this.client.mouseCoords) {
      if (this.clickItem()) {
        return;
      }

      // Check tile specs for chests and chairs
      const tileSpec = this.client.mapRenderer.getTileSpecAt(
        this.client.mouseCoords,
      );

      if (tileSpec !== undefined) {
        if (tileSpec === MapTileSpec.Chest) {
          this.client.chestController.openChest(this.client.mouseCoords);
          return;
        }

        if (
          this.client.mapController.isFacingChairAt(this.client.mouseCoords) &&
          !this.client.mapController.occupied(this.client.mouseCoords)
        ) {
          const coords = new Coords();
          coords.x = this.client.mouseCoords.x;
          coords.y = this.client.mouseCoords.y;
          this.client.mapController.sitChair(coords);
          return;
        }
      }
    }

    const npcAt = getNpcIntersecting(this.client.mousePosition!);
    if (npcAt) {
      const npc = this.client.nearby.npcs.find((n) => n.index === npcAt.id);
      if (npc) {
        this.client.npcController.clickNpc(npc);
        return;
      }
    }

    const characterAt = getCharacterIntersecting(this.client.mousePosition!);
    if (characterAt) {
      const character = this.client.getCharacterById(characterAt.id);
      if (character) {
        this.client.npcController.clickCharacter(character);
        return;
      }
    }

    const doorAt = getDoorIntersecting(this.client.mousePosition!);
    const door = doorAt ? this.client.mapController.getDoor(doorAt) : undefined;
    if (door && !door.open) {
      this.client.mapController.openDoor(doorAt!);
      return;
    }

    const lockerAt = getLockerIntersecting(this.client.mousePosition!);
    if (lockerAt) {
      this.client.lockerController.openLocker(lockerAt!);
      return;
    }

    const signAt = getSignIntersecting(this.client.mousePosition!);
    if (signAt) {
      const sign = this.client.mapRenderer.getSign(signAt.x, signAt.y);
      if (sign) {
        this.client.alertController.show(sign.title, sign.message);
        return;
      }
    }

    const boardAt = getBoardIntersecting(this.client.mousePosition!);
    if (boardAt) {
      const boardSpec = this.client.mapController.boardAt(boardAt);
      if (boardSpec !== undefined) {
        this.client.boardController.openBoard(boardSpec - MapTileSpec.Board1);
      }
      return;
    }

    const jukeboxAt = getJukeboxIntersecting(this.client.mousePosition!);
    if (jukeboxAt) {
      this.client.jukeboxController.openJukebox(jukeboxAt);
      return;
    }

    // A moving spell target can leave the cursor over the ground. That click
    // must not replace the chant animation with an auto-walk animation.
    if (
      this.client.spellController.selectedSpellId ||
      this.client.spellController.hasPendingSpellAction()
    ) {
      return;
    }

    const resource=this.client.mousePosition?this.client.mapRenderer.lifeResourceAt(this.client.mousePosition):undefined;
    if(resource){
      const player=this.client.getPlayerCoords();
      if(Math.abs(player.x-resource.x)+Math.abs(player.y-resource.y)>1){
        // Distant scenery clicks are movement, not a rejected gathering action.
        // Tree trunks and water are blocked, so walk to a reachable bank/edge.
        const candidates=[[0,-1],[1,0],[0,1],[-1,0]].map(([dx,dy])=>{
          const at=new Coords();at.x=resource.x+dx;at.y=resource.y+dy;return at;
        }).sort((a,b)=>(Math.abs(a.x-player.x)+Math.abs(a.y-player.y))-(Math.abs(b.x-player.x)+Math.abs(b.y-player.y)));
        for(const at of candidates){
          if(!this.client.mapController.canWalk(at,true))continue;
          const path=this.client.mapController.findPathTo(at);
          if(!path.length)continue;
          this.client.animationController.cursorClickAnimation=new CursorClickAnimation(at);
          this.client.npcController.cancelAutoAttack();this.client.movementController.autoWalkPath=path;break;
        }
        return; // No gathering request, warning, or popup on mouse OR touch.
      }
      this.client.movementController.autoWalkPath=[];
      this.client.npcController.cancelAutoAttack();
      if(resource.graphic==='campfire')openLifeCampfire(this.client,resource.id);
      else sendLifeAction(this.client,'start',resource.id);
      return; // Both mouse and touch must not fall through to walking.
    }

    if (
      !this.client.animationController.cursorClickAnimation &&
      this.client.mouseCoords &&
      this.client.mapController.canWalk(this.client.mouseCoords, true)
    ) {
      this.client.animationController.cursorClickAnimation =
        new CursorClickAnimation(this.client.mouseCoords);

      const path = this.client.mapController.findPathTo(
        this.client.mouseCoords,
      );
      if (path.length) {
        this.client.npcController.cancelAutoAttack();
        this.client.movementController.autoWalkPath = path;
      }
    }
  }

  private clickItem(): boolean {
    if (!this.client.mapController.cursorInDropRange()) return false;

    // Check for items first
    const itemsAtCoords = this.client.nearby.items.filter(
      (i) =>
        i.coords.x === this.client.mouseCoords!.x &&
        i.coords.y === this.client.mouseCoords!.y,
    );

    if (itemsAtCoords.length) {
      itemsAtCoords.sort((a, b) => b.uid - a.uid);

      const protectedItems = itemsAtCoords.filter((i) => {
        const p = this.client.itemProtectionController.itemProtectionTimers.get(
          i.uid,
        );
        return p && p.ticks > 0 && p.ownerId !== this.client.playerId;
      });

      if (protectedItems.length < itemsAtCoords.length) {
        const item = itemsAtCoords.find((i) => {
          const p =
            this.client.itemProtectionController.itemProtectionTimers.get(
              i.uid,
            );
          return !p || p.ticks === 0 || p.ownerId === this.client.playerId;
        });

        if (item) {
          const packet = new ItemGetClientPacket();
          packet.itemIndex = item.uid;
          this.client.bus!.send(packet);
        }

        return true;
      }

      const protectedItem = protectedItems[0];
      const protection =
        this.client.itemProtectionController.itemProtectionTimers.get(
          protectedItem.uid,
        );

      if (protection) {
        const owner = protection.ownerId
          ? this.client.getCharacterById(protection.ownerId)
          : undefined;

        const message = owner
          ? `${this.client.getResourceString(
              EOResourceID.STATUS_LABEL_ITEM_PICKUP_PROTECTED_BY,
            )} ${capitalize(owner.name)}`
          : this.client.getResourceString(
              EOResourceID.STATUS_LABEL_ITEM_PICKUP_PROTECTED,
            );

        this.client.toastController.showWarning(message);
        return true;
      }
    }

    return false;
  }

  handleRightClick(e: MouseEvent): void {
    const ui = document.getElementById('ui')!;
    if (
      this.client.state !== GameState.InGame ||
      this.client.typing ||
      (e.target !== ui && ui.contains(e.target as Node))
    ) {
      return;
    }

    const characterAt = getCharacterIntersecting(this.client.mousePosition!);
    if (characterAt) {
      const character = this.client.getCharacterById(characterAt.id);
      if (character) {
        if (characterAt.id === this.client.playerId) {
          this.client.socialController.requestPaperdoll(this.client.playerId);
        } else {
          this.client.menuPlayerId = character.playerId;
        }
        return;
      }
    }

    if (this.client.menuPlayerId) {
      this.client.menuPlayerId = 0;
    }
  }

  getHoveredPlayerMenuItem(): PlayerMenuItem | undefined {
    if (!this.client.mousePosition || !this.client.menuPlayerId) {
      return;
    }

    const rect = getCharacterRectangle(this.client.menuPlayerId);
    if (!rect) {
      return;
    }

    const menuX = rect.position.x + rect.width + 10;

    if (
      this.client.mousePosition.x < menuX ||
      this.client.mousePosition.x > menuX + PLAYER_MENU_WIDTH
    ) {
      return;
    }

    const relativeY =
      this.client.mousePosition.y - rect.position.y - PLAYER_MENU_OFFSET_Y;
    const itemIndex = Math.floor(relativeY / PLAYER_MENU_ITEM_HEIGHT);
    return itemIndex in PlayerMenuItem ? itemIndex : undefined;
  }
}
